/**
 * HOW WIDELY A NAME IS CARRIED — the line above NAMED IN, and the bar beside each scripture.
 *
 * The panel already listed which scriptures name a figure and how many passages each holds. As a
 * column of numbers that is a lookup: to learn that Adam is carried far more heavily in one place
 * than another you have to read every row and do the arithmetic. Their panel answers it at a glance
 * with a bar per scripture and a sentence above the list, and both are worth having.
 *
 * WHAT IS PORTED AND WHAT IS NOT. The sentence's shape is theirs (engine.js 2200) and so is the
 * bar's pixel range. The RATE is not portable, and this is the interesting part:
 *
 *   Their counts are invented — `mulberry32(f.id*131+7)` over `f.w*f.w*5+9` (engine.js 2188-2192) —
 *   so they land in a narrow band, and a flat `4 + count/9` spreads that band nicely across 8..56px.
 *   Ours are real passage counts and span 0 to 5,643. Measured over every multi-scripture figure in
 *   the build, that same rate puts 80.3% of bars flat on the 8px floor and saturates 1.9% — which is
 *   to say it would answer "how widely is this name carried" with one length for four rows in five.
 *
 * So ours scales each bar against the largest count ON THAT PANEL, which is the comparison a reader
 * is actually making: not "is this a lot of passages in absolute terms" but "where is this figure
 * carried most". Their own code computes exactly that maximum on engine.js:2191 and then never uses
 * it, so this is less a departure than the version their source half-reaches for. Both rules live
 * here, `THEIR_BAR` is checked against the engine line by line in the test beside this, and swapping
 * ours for theirs is one argument — which is what keeps the claim above falsifiable rather than a
 * story told once in a comment.
 */

/** A rule for turning a passage count into a bar width in CSS pixels. */
export type BarScale =
  /** A flat rate: so many pixels per passage, clamped. Theirs. */
  | { readonly kind: 'rate'; readonly min: number; readonly max: number; readonly base: number; readonly per: number }
  /** A share of the widest bar on the same panel. Ours, because our counts are real. */
  | { readonly kind: 'share'; readonly min: number; readonly max: number };

/** engine.js:2197 — `Math.max(8,Math.min(56,4+cnt/9))`. */
export const THEIR_BAR: BarScale = { kind: 'rate', min: 8, max: 56, base: 4, per: 9 };

/** The same 8..56px range they draw in, spent on the spread that is actually in front of the reader. */
export const OUR_BAR: BarScale = { kind: 'share', min: 8, max: 56 };

/**
 * The width of one bar, in CSS pixels.
 *
 * `largest` is the biggest count among the rows drawn beside this one; the rate rule ignores it.
 */
export function barWidth(count: number, largest: number, scale: BarScale): number {
  if (scale.kind === 'rate') {
    return Math.max(scale.min, Math.min(scale.max, scale.base + count / scale.per));
  }
  /* A figure whose every scripture holds nothing has no shape to show, so every bar sits at the floor
     rather than one of them arbitrarily winning a division by zero. */
  if (largest <= 0) return scale.min;
  const share = Math.min(1, Math.max(0, count / largest));
  return scale.min + (scale.max - scale.min) * share;
}

/** The widest count among rows drawn together — what a `share` bar is measured against. */
export function largestCount(counts: Iterable<number>): number {
  let most = 0;
  for (const count of counts) if (count > most) most = count;
  return most;
}

/**
 * Which sentence the list gets. The words are the dictionary's, in whichever language is on; only the
 * choice between them is mechanism, and that much is theirs.
 */
export type Spread = 'all' | 'only' | 'some';

export function spread(named: number, total: number): Spread {
  if (named >= total) return 'all';
  if (named === 1) return 'only';
  return 'some';
}
